import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { X, ChevronRight } from 'lucide-react'
import { useTutorial } from '../context/TutorialContext'
import { useI18n } from '../i18n/index.jsx'
import { authenticatedFetch } from '../hooks/useAuth'

const STEPS = {
  dashboard: [
    {
      titleKey: 'tutorial.dashboard.welcome',
      descKey: 'tutorial.dashboard.welcome.desc',
      target: null,
      position: 'center'
    },
    {
      titleKey: 'tutorial.dashboard.storage',
      descKey: 'tutorial.dashboard.storage.desc',
      target: 'storage-section',
      position: 'bottom'
    },
    {
      titleKey: 'tutorial.dashboard.requests',
      descKey: 'tutorial.dashboard.requests.desc',
      target: 'requests-section',
      position: 'bottom'
    },
    {
      titleKey: 'tutorial.dashboard.permissions',
      descKey: 'tutorial.dashboard.permissions.desc',
      target: 'permissions-section',
      position: 'top'
    }
  ],
  souvenirs: [
    {
      titleKey: 'tutorial.souvenirs.title',
      descKey: 'tutorial.souvenirs.title.desc',
      target: null,
      position: 'center'
    },
    {
      titleKey: 'tutorial.souvenirs.manage',
      descKey: 'tutorial.souvenirs.manage.desc',
      target: null,
      position: 'center'
    },
    {
      titleKey: 'tutorial.souvenirs.export',
      descKey: 'tutorial.souvenirs.export.desc',
      target: null,
      position: 'center',
      isVideo: true,
      videoUrl: 'https://www.youtube.com/embed/e3sTiK8pguQ?si=B1iRAhLyrxldTRUm'
    }
  ],
  extension: [
    {
      titleKey: 'tutorial.extension.install',
      descKey: 'tutorial.extension.install.desc',
      target: null,
      position: 'center'
    },
    {
      titleKey: 'tutorial.extension.tutorial',
      descKey: 'tutorial.extension.tutorial.desc',
      target: null,
      position: 'center',
      isVideo: true,
      videoUrl: 'https://www.youtube.com/embed/QobWMmq9Z54'
    },
    {
      titleKey: 'tutorial.extension.activate',
      descKey: 'tutorial.extension.activate.desc',
      target: null,
      position: 'center'
    },
    {
      titleKey: 'tutorial.extension.final',
      descKey: 'tutorial.extension.final.desc',
      target: null,
      position: 'center',
      isFinal: true
    }
  ]
}

export default function DashboardTutorial() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const { isActive, setIsActive, currentPage, setCurrentPage, currentStep, setCurrentStep } = useTutorial()

  // TOUS LES HOOKS AVANT LE RETURN
  useEffect(() => {
    // Ajouter des IDs aux sections
    const addIdToSection = (selector, id) => {
      const element = document.querySelector(selector)
      if (element && !element.id) {
        element.id = id
      }
    }

    setTimeout(() => {
      addIdToSection('[class*="storage"]', 'storage-section')
      addIdToSection('[class*="requests"]', 'requests-section')
      addIdToSection('[class*="permissions"]', 'permissions-section')
    }, 100)
  }, [])

  useEffect(() => {
    // Synchroniser la page avec l'URL
    if (location.pathname === '/dashboard') {
      setCurrentPage('dashboard')
    } else if (location.pathname === '/souvenirs') {
      setCurrentPage('souvenirs')
    } else if (location.pathname === '/extension') {
      setCurrentPage('extension')
    }
  }, [location.pathname, setCurrentPage])

  // Maintenant on peut faire le return null
  if (!isActive) return null

  const currentStepData = STEPS[currentPage][currentStep]
  const totalSteps = STEPS[currentPage].length

  const handleNext = () => {
    if (currentStep === totalSteps - 1) {
      if (currentPage === 'dashboard') {
        setCurrentPage('souvenirs')
        setCurrentStep(0)
        navigate('/souvenirs')
      } else if (currentPage === 'souvenirs') {
        setCurrentPage('extension')
        setCurrentStep(0)
        navigate('/extension')
      } else {
        handleComplete()
      }
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    } else if (currentPage !== 'dashboard') {
      if (currentPage === 'souvenirs') {
        setCurrentPage('dashboard')
        setCurrentStep(STEPS.dashboard.length - 1)
        navigate('/dashboard')
      } else if (currentPage === 'extension') {
        setCurrentPage('souvenirs')
        setCurrentStep(STEPS.souvenirs.length - 1)
        navigate('/souvenirs')
      }
    }
  }

  const handleComplete = async () => {
    try {
      // Envoyer une requête POST pour marquer le tutoriel comme terminé
      const response = await authenticatedFetch('http://127.0.0.1:8000/auth/tuto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tuto: true })
      })

      if (!response.ok) {
        console.error('Erreur lors de la mise à jour du tutoriel:', response.status)
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du statut tutoriel:', error)
    } finally {
      localStorage.setItem('tutorial_completed', 'true')
      setIsActive(false)
    }
  }

  const handleSkip = async () => {
    try {
      // Envoyer une requête POST pour marquer le tutoriel comme terminé
      const response = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/tuto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tuto: true })
      })

      if (!response.ok) {
        console.error('Erreur lors de la mise à jour du tutoriel:', response.status)
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du statut tutoriel:', error)
    } finally {
      localStorage.setItem('tutorial_completed', 'true')
      setIsActive(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={handleSkip}
      />

      {/* Cadre lumineux */}
      {currentStepData.target && (() => {
        const target = document.getElementById(currentStepData.target)
        if (!target) return null
        const rect = target.getBoundingClientRect()
        return (
          <div
            className="absolute pointer-events-none z-51 border-4 border-emerald-500 rounded-xl animate-pulse"
            style={{
              top: `${rect.top + window.scrollY - 8}px`,
              left: `${rect.left - 8}px`,
              width: `${rect.width + 16}px`,
              height: `${rect.height + 16}px`,
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.6), inset 0 0 20px rgba(16, 185, 129, 0.3)'
            }}
          />
        )
      })()}

      {/* Popup */}
      <div
        className="fixed z-52 w-full max-w-md p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-emerald-500/30 rounded-2xl shadow-2xl"
        style={{
          top: currentStepData.position === 'center' ? '50%' : 'auto',
          bottom: currentStepData.position === 'top' ? '20px' : 'auto',
          left: '50%',
          transform: currentStepData.position === 'center' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
          ...(currentStepData.position === 'bottom' && { top: '20px' })
        }}
      >
        {/* Bouton fermer */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition rounded-lg hover:bg-white/10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Contenu */}
        <div className="space-y-4">
          {/* Barre de progression */}
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-emerald-500'
                    : index < currentStep
                    ? 'bg-emerald-500/50'
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>

          {/* Compteur */}
          <div className="text-xs text-slate-500 text-center">
            {currentPage === 'dashboard' && 'Tableau de bord'}
            {currentPage === 'souvenirs' && 'Souvenirs'}
            {currentPage === 'extension' && 'Extension'}
            {' • '}
            {currentStep + 1} / {totalSteps}
          </div>

          {/* Titre */}
          <h3 className="text-2xl font-bold text-white">{t(currentStepData.titleKey)}</h3>

          {/* Description */}
          <p className="text-slate-300 leading-relaxed">{t(currentStepData.descKey)}</p>

          {/* Vidéo YouTube si présente */}
          {currentStepData.isVideo && currentStepData.videoUrl && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black mt-4">
              <iframe
                width="100%"
                height="100%"
                src={currentStepData.videoUrl}
                title={t(currentStepData.titleKey)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}

          {/* Boutons */}
          <div className="flex items-center justify-between pt-4 gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0 && currentPage === 'dashboard'}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← {t('tutorial.button.previous')}
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 text-sm font-semibold bg-emerald-500 text-slate-950 rounded-lg hover:bg-emerald-400 transition"
            >
              {currentPage === 'extension' && currentStep === totalSteps - 1 ? (
                `${t('tutorial.button.done')} ✓`
              ) : (
                <>
                  {t('tutorial.button.next')} <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>

          {/* Carte de recommandation finale */}
          {currentPage === 'extension' && currentStep === totalSteps - 1 && (
            <div className="mt-6 pt-4 border-t border-emerald-500/20 bg-emerald-500/5 rounded-lg p-4">
              <p className="text-sm text-slate-300 font-semibold">Enjoy Hiperyon! 🚀</p>
            </div>
          )}

          {/* Skip */}
          <button
            onClick={handleSkip}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-300 transition pt-2"
          >
            {t('tutorial.button.skip')}
          </button>
        </div>
      </div>
    </div>
  )
}

